import React from 'react';
import { Check, ImageIcon, ExternalLink } from 'lucide-react';
import { StandardButton } from '@/styles/StandardButtons';
import { Checkbox } from '@/components/ui/checkbox';
import { formatMoney } from '@/lib/line-items';
import { toSafeHref } from '@/lib/safe-url';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { ProtectedImage } from '@/components/shared/ProtectedImage';

/**
 * What an option card needs -- deliberately WITHOUT builder_price: this card
 * is the audience-neutral tile the Client Portal will reuse as is. Whoever
 * renders it only ever hands it the Client Price.
 */
export interface OptionCardData {
  id: string;
  name: string;
  description?: string | null;
  link: string | null;
  client_price: number;
  is_selected: boolean;
}

/**
 * The prices row of an option tile: "Builder Price: $X  Client Price: $Y". `builderPrice` is only handed over by an audience that may see it (the builder's
 * own screens); a screen for the client passes none and the same row shows just the Client Price -- no second component, no hidden-by-CSS figure.
 */
export function OptionPrices({ builderPrice, clientPrice }: { builderPrice?: number; clientPrice: number }) {
  return (
    <div className="flex flex-wrap gap-x-4 text-sm" data-testid="option-prices">
      {builderPrice !== undefined && (
        <span className="text-muted-foreground">Builder Price: <span className="font-semibold text-foreground">{formatMoney(builderPrice)}</span></span>
      )}
      <span className="text-muted-foreground">Client Price: <span className="font-semibold text-foreground">{formatMoney(clientPrice)}</span></span>
    </div>
  );
}

interface SelectionOptionCardProps {
  option: OptionCardData;
  /** Shown beside the Client Price only when given (see OptionPrices). */
  builderPrice?: number;
  /** Selection mode: a checkbox in the tile's corner. It only marks the tile for an action (copy, delete...); it is NOT the option's approved state. */
  selectable?: { checked: boolean; onChange: (checked: boolean) => void };
  thumbnail?: GenericAttachment;
  /** The title becomes a button that opens the option (its own modal). Omit and the title is plain text. */
  onOpen?: (option: OptionCardData) => void;
  /** Approve / un-approve (select / deselect). Omit to render read-only. */
  onToggle?: (option: OptionCardData) => void;
  disabled?: boolean;
}

/**
 * One option as a tile: fixed-size thumbnail on the left (a placeholder when
 * there is no attachment -- the layout never shifts), then Title, Link and
 * Client Price on three always-present lines so every tile has the same
 * height, and the Approve / Approved control on the right.
 */
export function SelectionOptionCard({ option, builderPrice, selectable, thumbnail, onOpen, onToggle, disabled }: SelectionOptionCardProps) {
  // The link is stored as typed; it only becomes clickable when it is a safe http(s) address (lib/safe-url.ts).
  const href = toSafeHref(option.link);
  const thumbId = thumbnail?.id && (thumbnail.file_type || '').startsWith('image/') ? thumbnail.id : null;

  return (
    <div
      className={`relative flex items-center gap-4 rounded-lg border-2 p-3 ${option.is_selected ? 'border-primary bg-muted/40' : 'border-border bg-card'}`}
      data-testid={`option-card-${option.id}`}
    >
      {selectable && (
        <Checkbox
          checked={selectable.checked}
          onCheckedChange={(checked) => selectable.onChange(checked === true)}
          aria-label={`Select ${option.name}`}
          className="absolute left-2 top-2 z-10 bg-card"
        />
      )}
      <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
        {thumbId ? (
          <ProtectedImage kind="attachment" id={thumbId} alt={option.name} className="h-full w-full object-cover" fallback={<ImageIcon className="h-7 w-7 text-muted-foreground" aria-label="No image" />} />
        ) : (
          <ImageIcon className="h-7 w-7 text-muted-foreground" aria-label="No image" />
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        {onOpen ? (
          <button type="button" className="block max-w-full truncate text-left font-semibold underline-offset-2 hover:underline" title={option.name} onClick={() => onOpen(option)}>{option.name}</button>
        ) : (
          <div className="truncate font-semibold" title={option.name}>{option.name}</div>
        )}
        {/* Fixed two-line slot: every tile keeps the same height whether or not there is a description. */}
        <div className="line-clamp-2 h-10 text-sm text-muted-foreground" title={option.description ?? undefined}>{option.description}</div>
        <div className="h-5 truncate text-sm">
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
              <ExternalLink className="h-3.5 w-3.5" />
              View link
            </a>
          ) : option.link ? (
            // Not an address a browser can open (free text): shown as typed, not clickable.
            <span className="text-muted-foreground" title={option.link}>{option.link}</span>
          ) : (
            <span className="text-muted-foreground">No link</span>
          )}
        </div>
        <OptionPrices builderPrice={builderPrice} clientPrice={option.client_price} />
      </div>

      {onToggle && (
        <StandardButton
          type="button"
          customVariant={option.is_selected ? 'white' : 'blue'}
          disabled={disabled}
          onClick={() => onToggle(option)}
          aria-pressed={option.is_selected}
          className="flex-shrink-0 gap-1"
        >
          {option.is_selected && <Check className="h-4 w-4" />}
          {option.is_selected ? 'Approved' : 'Approve'}
        </StandardButton>
      )}
    </div>
  );
}
